import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Plus, Trash2 } from 'lucide-react';
import type { ITemplateField, TTemplateFieldType } from '@/@types/superadmin';

interface ITemplateFieldBuilderProps {
  // LOCAL_TYPE_JUSTIFIED: component-local prop interface, no domain reuse
  fields: ITemplateField[];
  onChange: (fields: ITemplateField[]) => void;
}

const FIELD_TYPES: TTemplateFieldType[] = ['text', 'number', 'date', 'boolean', 'select', 'file'];

export function TemplateFieldBuilder({ fields, onChange }: ITemplateFieldBuilderProps) {
  const addField = () => {
    onChange([...fields, { label: '', fieldType: 'text', isRequired: false }]);
  };

  const updateField = (index: number, updates: Partial<ITemplateField>) => {
    const updated = fields.map((f, i) => (i === index ? { ...f, ...updates } : f));
    onChange(updated);
  };

  const removeField = (index: number) => {
    onChange(fields.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Fields</span>
        <Button variant="outline" size="sm" onClick={addField}>
          <Plus className="w-3.5 h-3.5 mr-1" />
          Add Field
        </Button>
      </div>
      {fields.map((field, index) => (
        <div
          key={index}
          className="flex items-center gap-2 p-3 border border-border rounded-lg bg-muted/20"
        >
          <Input
            placeholder="Field label"
            value={field.label}
            onChange={e => updateField(index, { label: e.target.value })}
            className="flex-1"
          />
          <Select
            value={field.fieldType}
            onValueChange={v => updateField(index, { fieldType: v as TTemplateFieldType })} // AS_JUSTIFIED: one-time cast from Radix Select string value to typed enum
          >
            <SelectTrigger className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FIELD_TYPES.map(t => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1.5">
            <Switch
              checked={field.isRequired}
              onCheckedChange={v => updateField(index, { isRequired: v })}
            />
            <span className="text-xs text-muted-foreground">Req</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => removeField(index)}>
            <Trash2 className="w-3.5 h-3.5 text-destructive" />
          </Button>
        </div>
      ))}
      {fields.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">
          No fields defined. Click "Add Field" to start.
        </p>
      )}
    </div>
  );
}
